import { useQuery } from '@tanstack/react-query';
import { LayoutGroup, motion, useReducedMotion } from 'framer-motion';
import { Link, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { getStandings } from '../api/standings.ts';
import { claves } from '../api/queries.ts';
import { ApiErrorState } from '../components/ApiErrorState.tsx';
import { Cargando } from '../components/FullScreenMessage.tsx';
import { NotFoundPage } from './NotFoundPage.tsx';
import { useAuth } from '../features/auth/useAuth.ts';
import { useLeagueDetail } from '../features/leagues/hooks.ts';
import { formatDate, machineDateTime } from '../lib/datetime.ts';
import { formatearPuntos } from '../lib/puntos.ts';
import { RachaBadge } from '../features/standings/RachaBadge.tsx';
import { useMovimientos } from '../features/standings/useMovimientos.ts';
import { RecapPanel } from '../features/recap/RecapPanel.tsx';

export function StandingsPage() {
  const { t, i18n } = useTranslation();
  const { leagueId } = useParams<{ leagueId: string }>();
  const { user } = useAuth();
  // Quien tiene el sistema configurado para reducir movimiento no recibe ninguna
  // animación: el dato es el mismo, y aquí el movimiento es adorno, no información.
  const sinMovimiento = useReducedMotion();

  const liga = useLeagueDetail(leagueId ?? '');
  const { data, isPending, error } = useQuery({
    queryKey: claves.ligaClasificacion(leagueId ?? ''),
    queryFn: () => getStandings(leagueId ?? ''),
    enabled: Boolean(leagueId),
  });

  const filas = data?.standings;
  const { movimientos, animar } = useMovimientos(filas);

  if (!leagueId) return <NotFoundPage />;
  if (error) return <ApiErrorState error={error} />;
  if (liga.error) return <ApiErrorState error={liga.error} />;
  if (isPending) return <Cargando text={t('common.loading')} />;

  const tabla = filas ?? [];
  const umbrales = data?.streak_thresholds ?? [];
  const nadiePuntua = tabla.every((f) => f.points_centi === 0);
  // La animación pide DOS condiciones: que haya una carga anterior con la que comparar
  // (`animar`) y que el usuario no haya pedido menos movimiento.
  const conAnimacion = animar && !sinMovimiento;

  return (
    <>
      <header className="mb-4">
        <Link to={`/leagues/${leagueId}`} className="text-sm text-tenue">
          ← {liga.data?.league.name ?? t('common.back')}
        </Link>
        <h1 className="mt-1 text-xl font-bold">{t('standings.title')}</h1>
      </header>

      {nadiePuntua ? (
        <p className="aviso-info mb-3" data-test="clasificacion-vacia">
          {t('standings.empty')}
        </p>
      ) : null}

      {/*
        Lista y no <table>: en un móvil de 360px una tabla con cinco columnas obliga a
        desplazamiento horizontal o encoge la fuente por debajo de lo legible. Lo que
        se pierde es la cabecera de columnas, y aquí no hace falta: cada dato lleva su
        etiqueta al lado.

        Los componentes del desempate (exactos y aciertos de ganador) van en la línea
        secundaria a propósito. Son el CONTEXTO que explica un empate, no el dato
        central: quien mira la clasificación quiere ver puestos y puntos.
      */}
      <LayoutGroup>
        <ol className="list-none space-y-2 p-0" data-test="clasificacion" data-animando={String(conAnimacion)}>
          {tabla.map((fila) => {
            const soyYo = fila.user_id === user?.id;
            const movimiento = movimientos.get(fila.user_id) ?? null;
            return (
              <motion.li
                key={fila.user_id}
                /*
                  `layout` va SIEMPRE encendido, y no es lo que parece a primera vista.
                  Framer crea el nodo de proyección —el que mide las posiciones— al
                  MONTAR el componente. Si el elemento nace con `layout={false}` y se
                  enciende después, no hay nada medido y no se anima nunca: comprobado,
                  el recorrido E2E no veía ni un `transform` y Framer ni siquiera ponía
                  `data-projection-id`.

                  Lo que se apaga es la TRANSICIÓN, no la medición: con duración 0 el
                  cambio es instantáneo, que es exactamente "no animar".

                  Honestidad sobre lo que hace cada cosa. Que la primera carga no anime
                  NO lo garantiza esta condición, sino que solo se llega aquí con los
                  datos ya cargados: la lista nace con todo en su sitio y no hay posición
                  anterior desde la que saltar. Quitar la condición no lo nota nadie, y se
                  comprobó rompiéndolo. Lo que sí sostiene esta rama es el respeto a
                  `prefers-reduced-motion`, y eso sí tiene su comprobación en el recorrido.
                */
                layout="position"
                transition={
                  conAnimacion
                    ? { type: 'spring', stiffness: 500, damping: 40 }
                    : { duration: 0 }
                }
                className={[
                  'tarjeta flex items-start gap-3',
                  soyYo ? 'border-acento' : '',
                  fila.tied ? 'border-l-4 border-l-aviso' : '',
                ].join(' ')}
                data-test="fila-clasificacion"
                data-username={fila.username}
                data-rank={fila.rank}
                data-tied={String(fila.tied)}
                data-movimiento={movimiento ?? ''}
              >
                <span
                  className="w-8 shrink-0 text-center text-lg font-bold tabular-nums text-tenue"
                  data-test="puesto"
                >
                  {fila.rank}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="truncate font-semibold">{fila.username}</span>
                    {soyYo ? (
                      <span className="text-xs text-tenue">({t('standings.you')})</span>
                    ) : null}
                    {/*
                      El distintivo se CALCULA desde la racha viva. No hay tabla de
                      logros: si la racha se rompe, desaparece solo.
                    */}
                    <RachaBadge racha={fila.current_streak} umbrales={umbrales} />
                    {/*
                      El empate se marca, pero NO se inventa un desempate visual: el
                      backend devuelve el mismo `rank` para los dos y aquí se enseña el
                      mismo puesto. Ordenarlos por algo que el backend no ha decidido
                      sería darle significado al criterio técnico de estabilidad (D5.1).
                    */}
                    {fila.tied ? (
                      <span
                        className="rounded-full border border-aviso px-2 py-0.5 text-xs text-aviso"
                        title={t('standings.tiedExplained')}
                        data-test="marca-empate"
                      >
                        {t('standings.tied')}
                      </span>
                    ) : null}
                    {movimiento === 'sube' || movimiento === 'baja' ? (
                      <span
                        className={`text-xs ${movimiento === 'sube' ? 'text-acento' : 'text-tenue'}`}
                        title={t(`streak.moved.${movimiento === 'sube' ? 'up' : 'down'}`)}
                        data-test="flecha-movimiento"
                      >
                        {movimiento === 'sube' ? '▲' : '▼'}
                      </span>
                    ) : null}
                  </span>

                  <span className="mt-0.5 block text-xs text-tenue" data-test="desempate">
                    {fila.exact_hits} {t('standings.exactHits')} · {fila.winner_hits}{' '}
                    {t('standings.winnerHits')} · {fila.settled_matches}{' '}
                    {t('standings.settledMatches')}
                  </span>

                  <span className="mt-0.5 block text-xs text-tenue" data-test="racha-linea">
                    {t('streak.label')}:{' '}
                    <span data-test="racha-valor" data-racha={fila.current_streak}>
                      {fila.current_streak}
                    </span>
                  </span>

                  {fila.reached_total_at ? (
                    <time
                      className="mt-0.5 block text-xs text-tenue"
                      dateTime={machineDateTime(fila.reached_total_at)}
                      data-test="alcanzado"
                    >
                      {t('standings.reachedOn', {
                        date: formatDate(fila.reached_total_at, i18n.language),
                      })}
                    </time>
                  ) : null}
                </span>

                <span
                  className="shrink-0 text-right text-lg font-bold tabular-nums"
                  data-test="puntos"
                >
                  {formatearPuntos(fila.points_centi, i18n.language)}
                  <span className="block text-xs font-normal text-tenue">
                    {t('standings.points')}
                  </span>
                </span>
              </motion.li>
            );
          })}
        </ol>
      </LayoutGroup>

      <RecapPanel leagueId={leagueId} />
    </>
  );
}
